import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { BASE_URL } from '../config';
import { TokenService } from './TokenService';

class Request {
    baseURL: string;
    isRefreshing: boolean;
    failedRequests: Array<{ resolve: (value: any) => void; reject: (reason?: any) => void }>;
    tokenService: typeof TokenService;
    client: AxiosInstance;

    constructor() {
        this.baseURL = BASE_URL;
        this.isRefreshing = false;
        this.failedRequests = [];
        this.tokenService = TokenService;
        this.client = axios.create({ baseURL: this.baseURL });
        this.beforeRequest = this.beforeRequest.bind(this);
        this.onRequestFailure = this.onRequestFailure.bind(this);
        this.processQueue = this.processQueue.bind(this);
        this.client.interceptors.request.use(this.beforeRequest);
        this.client.interceptors.response.use(this.onRequestSuccess, this.onRequestFailure);
    }

    beforeRequest(request: InternalAxiosRequestConfig) {
        const token = this.tokenService.getAccessToken();
        if (token && request.headers) {
            (request.headers as any).Authorization = `Bearer ${token}`;
        }
        return request;
    }

    onRequestSuccess(response: AxiosResponse) {
        return response;
    }

    async onRequestFailure(err: any) {
        const response = err?.response;
                const status = response?.status;

        // Network error / CORS / server down — no response object at all.
        if (!response) {
            return Promise.reject(err);
        }

        // Only attempt refresh on 401 and only once per request.
        if (status === 401 && err.config && !err.config.__isRetryRequest) {
            if (this.isRefreshing) {
                try {
                    const token = await new Promise((resolve, reject) => {
                        this.failedRequests.push({ resolve, reject });
                    });
                    err.config.headers = err.config.headers ?? {};
                    err.config.headers.Authorization = `Bearer ${token}`;
                    return this.client(err.config);
                } catch (e) {
                    return Promise.reject(e);
                }
            }

            this.isRefreshing = true;
            err.config.__isRetryRequest = true;

            return new Promise((resolve, reject) => {
                this.tokenService.refreshAccessToken()
                    .then((token: string) => {
                        if (!token) {
                            this.isRefreshing = false;
                            this.processQueue(err, null);
                            return reject(err);
                        }
                        this.tokenService.setAccessToken(token);
                        err.config.headers = err.config.headers ?? {};
                        err.config.headers.Authorization = `Bearer ${token}`;
                        this.isRefreshing = false;
                        this.processQueue(null, token);
                        resolve(this.client(err.config));
                    })
                    .catch((e: AxiosError) => {
                        this.isRefreshing = false;
                        this.processQueue(e, null);
                        reject(err);
                    });
            });
        }

        // Re-throw the original error; callers in ApiRequestProvider handle it.
        return Promise.reject(err);
    }

    processQueue(error: AxiosError | null, token: string | null = null) {
        this.failedRequests.forEach((prom) => {
            if (error) prom.reject(error);
            else prom.resolve(token);
        });
        this.failedRequests = [];
    }
}

const request = new Request();
export default request.client;
